'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { CompanyIcpType } from '@hellodarwin/core/lib/features/entities/companies-entities';
import { useCompanyInfos } from '@hellodarwin/core/lib/features/providers/company-infos-provider';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { match } from 'path-to-regexp';
import React, { createContext, useCallback, useContext, useMemo, } from 'react';
import { determineCompanyIcpType } from '../components/marketing-controlled-buttons/rules/check-company-icp';
function flattenPages(pages, parentPath = '') {
    let flat = {};
    for (const [key, page] of Object.entries(pages)) {
        const fullPath = parentPath
            ? `${parentPath.replace(/\/$/, '')}/${key.replace(/^\//, '')}`
            : key;
        flat[fullPath] = page;
        if (page.children) {
            Object.assign(flat, flattenPages(page.children, fullPath));
        }
    }
    return flat;
}
const PremiumAccessContext = createContext(null);
const standardAccessOrder = [
    'free',
    'premium',
    'custom',
    'admin',
];
export const isAccessLevelAllowed = (accessLevel, requiredAccess) => {
    if (accessLevel === 'admin')
        return true;
    if (accessLevel === 'expert') {
        return requiredAccess === 'expert';
    }
    if (requiredAccess === 'expert')
        return false;
    return (standardAccessOrder.indexOf(accessLevel) >=
        standardAccessOrder.indexOf(requiredAccess));
};
export const PremiumAccessProvider = ({ children, accessLevel = 'free', pages, handleCheckout, }) => {
    const flatPages = React.useMemo(() => flattenPages(pages), [pages]);
    const { company } = useCompanyInfos();
    const isPageAccessible = useCallback((pathname) => {
        var _a;
        if (!accessLevel || !Object.keys(flatPages).length)
            return true;
        let matchedPage;
        for (const pattern in flatPages) {
            const matcher = match(pattern, { decode: decodeURIComponent });
            if (matcher(pathname)) {
                matchedPage = flatPages[pattern];
                break;
            }
        }
        if (!matchedPage)
            return true;
        const pageRequiredAccess = (_a = matchedPage.requiredAccess) !== null && _a !== void 0 ? _a : 'free';
        return isAccessLevelAllowed(accessLevel, pageRequiredAccess);
    }, [accessLevel, flatPages]);
    const isSectionAccessible = useCallback((requiredAccess) => {
        return isAccessLevelAllowed(accessLevel, requiredAccess);
    }, [accessLevel]);
    const { selectedLocale } = useLocale();
    const companyIcpType = useMemo(() => determineCompanyIcpType(selectedLocale, company === null || company === void 0 ? void 0 : company.annual_revenue, company === null || company === void 0 ? void 0 : company.province, company === null || company === void 0 ? void 0 : company.industry_sector, company === null || company === void 0 ? void 0 : company.legal_structure), [company, selectedLocale]);
    const companyIsICP = useMemo(() => {
        if (accessLevel === 'expert') {
            return false;
        }
        if (accessLevel === 'custom') {
            return false;
        }
        if (accessLevel === 'premium' &&
            companyIcpType === CompanyIcpType.Premium) {
            return false;
        }
        if (companyIcpType === CompanyIcpType.None) {
            return false;
        }
        return true;
    }, [companyIcpType, accessLevel]);
    return (_jsx(PremiumAccessContext.Provider, { value: {
            accessLevel,
            pages,
            isPageAccessible,
            isSectionAccessible,
            handleCheckout,
            companyIsICP,
            companyIcpType,
        }, children: children }));
};
export const usePremiumAccess = () => {
    const context = useContext(PremiumAccessContext);
    if (!context) {
        throw new Error('usePremiumAccess must be used within a PremiumAccessProvider');
    }
    return context;
};
export default PremiumAccessProvider;
